import { borderRadius, fontWeights } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent
} from '@mui/material'
import type { BlockedStatusOption, Stage } from './types'

interface ChangeStatusDialogProps {
  open: boolean
  currentStage: Stage | null
  fallbackStatus: string
  selectedStatus: string
  statusOptions: readonly string[]
  blockedStatusOptions?: readonly BlockedStatusOption[]
  isLoading?: boolean
  title?: string
  description?: string
  onStatusChange: (status: string) => void
  onClose: () => void
  onConfirm: () => void
}

export default function ChangeStatusDialog({
  open,
  currentStage,
  fallbackStatus,
  selectedStatus,
  statusOptions,
  blockedStatusOptions,
  isLoading,
  title = 'Confirm status change',
  description = 'Confirm you want to change the status',
  onStatusChange,
  onClose,
  onConfirm
}: ChangeStatusDialogProps) {
  const blockedReasonMap = new Map((blockedStatusOptions ?? []).map(option => [option.status, option.reason]))
  const availableStatuses = statusOptions.filter(status => !blockedReasonMap.has(status))
  const blockedStatuses = statusOptions.filter(status => blockedReasonMap.has(status))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='sectionTitle'>{title}</Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Typography variant='body2' mt={0.5}>
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant='fieldLabel' display='block' mb={0.5}>
            Current status:
          </Typography>
          <Typography variant='fieldValue' fontWeight={fontWeights.semibold}>
            {currentStage?.label ?? fallbackStatus ?? '-'}
          </Typography>
        </Box>

        <Box>
          <Typography variant='fieldLabel' display='block' mb={1}>
            New status
          </Typography>
          <FormControl fullWidth size='small'>
            <Select
              value={selectedStatus}
              onChange={(e: SelectChangeEvent) => onStatusChange(e.target.value)}
              displayEmpty
              sx={{ borderRadius: borderRadius.sm }}
            >
              {availableStatuses.map(status => {
                const isCurrent = status === (currentStage?.label ?? fallbackStatus)
                return (
                  <MenuItem key={status} value={status} disabled={isCurrent}>
                    {status}
                    {isCurrent && ' (Current)'}
                  </MenuItem>
                )
              })}

              {blockedStatuses.map(status => {
                const isCurrent = status === (currentStage?.label ?? fallbackStatus)
                const reason = blockedReasonMap.get(status)
                return (
                  <MenuItem key={status} value={status} disabled title={reason}>
                    {status}
                    {reason && ` (${reason})`}
                    {isCurrent && !reason && ' (Current)'}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant='ghost' onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='contained' onClick={onConfirm} disabled={!selectedStatus || isLoading} loading={isLoading}>
          Change status
        </Button>
      </DialogActions>
    </Dialog>
  )
}
